'use client';
import Image from 'next/image';
import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import { cn } from 'lib/utils';
import { type ImageField, type LinkField } from 'lib/types';
import { type IconNames } from 'atoms/Icon/Icons';
import { type BloomProps, Bloom, HeaderSection, Link, Icon, Wrapper } from 'ui';

export type MultiColumnCardsProps = {
  className?: string;
  title: string;
  intro?: string;
  cards: {
    title: string;
    intro: string;
    image: ImageField;
    cta: LinkField;
    icon?: IconNames;
  }[];
  clampCardTitles?: boolean;
  gridRows?: number;
  mobileImage?: ImageField;
  bloom?: BloomProps;
};

const MultiColumnCards = ({
  className,
  title,
  intro,
  cards,
  clampCardTitles,
  gridRows,
  mobileImage,
  bloom,
}: MultiColumnCardsProps) => {
  const hasMobileImage = !!mobileImage?.src;
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-10%' });

  return (
    <motion.section {...{ ref }}>
      <Wrapper
        className={cn('relative flex flex-col gap-12 lg:gap-20', className)}
      >
        <Bloom {...bloom} />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <HeaderSection
            content={intro}
            alignment="left"
            className="relative z-2"
            {...{ title }}
          />
        </motion.div>

        <div
          className={cn(
            'relative z-2 max-w-full grid grid-cols-1 gap-x-7.5 gap-y-18 lg:gap-y-20',
            {
              'md:grid-cols-2':
                (gridRows && gridRows > 1) || (!gridRows && cards.length > 1),
              'lg:grid-cols-3':
                (gridRows && gridRows > 2) || (!gridRows && cards.length > 2),
              'xl:grid-cols-4':
                (gridRows && gridRows > 3) || (!gridRows && cards.length > 3),
            }
          )}
        >
          {cards.map((card, index) => {
            const isExternal = card.cta?.target === '_blank';

            return (
              <motion.article
                className="relative flex flex-col"
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={
                  isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }
                }
                transition={{
                  duration: 0.6,
                  delay: 0.2 + index * 0.1,
                  ease: [0.25, 0.1, 0.25, 1],
                }}
              >
                {/* Regular card images - hidden on mobile when mobileImage is provided */}
                <div
                  className={cn('relative w-full aspect-[1.3/1] mb-8', {
                    'max-md:hidden': hasMobileImage,
                  })}
                >
                  <Image
                    src={card.image.src}
                    alt={card.image.alt}
                    fill={true}
                    className="absolute w-full h-auto mb-4 object-cover rounded-lg"
                    role={!card.image.alt ? 'presentation' : undefined}
                  />
                </div>

                {/* Mobile-specific image for first card only */}
                {index === 0 && hasMobileImage && (
                  <div className="relative w-full aspect-[1.3/1] mb-8 md:hidden">
                    <Image
                      src={mobileImage.src}
                      alt={mobileImage.alt}
                      fill={true}
                      className="absolute w-full h-auto mb-4 object-cover rounded-lg"
                      role={!card.image.alt ? 'presentation' : undefined}
                    />
                  </div>
                )}

                {card.icon && (
                  <div className="max-lg:mb-4 lg:hidden">
                    <span className="size-6 flex">
                      <Icon
                        name={card.icon}
                        className="size-auto fill-stone-100"
                      />
                    </span>
                  </div>
                )}

                <h3
                  className={cn('text-p1 mb-2', {
                    'line-clamp-1': clampCardTitles,
                  })}
                >
                  {card.title}
                </h3>

                <p className="text-neutral-200 flex-grow mb-6">{card.intro}</p>

                <p>
                  <Link
                    href={card.cta.href}
                    target={card.cta.target}
                    rel={card.cta.rel}
                    trackingProps={card.cta.trackingProps}
                    variant="neutral"
                    className="after:absolute after:inset-0"
                  >
                    <span>{card.cta.text || 'Learn more'}</span>
                    <Icon
                      name={
                        isExternal
                          ? 'external-link-arrow'
                          : 'carousel-arrow-right'
                      }
                      className={cn('inline-block h-auto ml-2 align-middle', {
                        'w-5': !isExternal,
                        'w-3': isExternal,
                      })}
                    />
                  </Link>
                </p>
              </motion.article>
            );
          })}
        </div>
      </Wrapper>
    </motion.section>
  );
};

export default MultiColumnCards;
